import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useNavigation } from '../../hooks/useNavigation';
import BackgroundShapes from '../common/BackgroundShapes';
import { userAPI } from '../../services/apiService';
import Avatar from '../common/Avatar';
import { occupations } from '../../constants/formData';
import ProfileInfoSections, { ProfileData } from './ProfileInfoSections';

// Format move-in date YYYY-MM-01 string to "June 2026" display label.
// Falls back to raw string for legacy enum values (e.g. "immediately").
const formatMoveInDate = (date: string | Date): string => {
  if (!date) return '';
  const d = new Date(typeof date === 'string' ? date + 'T00:00:00' : date);
  if (isNaN(d.getTime())) return typeof date === 'string' ? date : '';
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

const mapUserToProfileData = (userData: any): ProfileData => ({
  bio: userData.personalInfo?.bio || '',
  location: userData.housingInfo?.selectedLocations || [],
  budget: userData.housingInfo?.rentPreferences
    ? `$${userData.housingInfo.rentPreferences.minRent}-${userData.housingInfo.rentPreferences.maxRent}`
    : 'Not specified',
  housingSituation: userData.housingInfo?.housingSituation || '',
  moveInDate: userData.housingInfo?.moveInDate
    ? formatMoveInDate(userData.housingInfo.moveInDate)
    : '',
  occupation: userData.occupation || '',
  languages: userData.languages || [],
  cleanliness: userData.lifestyle?.cleanliness || '',
  socialLevel: userData.lifestyle?.socialLevel || '',
  workSchedule: userData.lifestyle?.workSchedule || '',
  hasPets: userData.lifestyle?.hasPets || [],
  okWithPets: userData.lifestyle?.okWithPets || [],
});

const ProfileViewPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { token } = useAuth();
  const navigation = useNavigation();
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [profilePictureUrl, setProfilePictureUrl] = useState<string | null | undefined>(undefined);
  const [viewedUserName, setViewedUserName] = useState<{ firstName: string; lastName: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!userId || !token) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const response = await userAPI.getProfile(userId, token);
        if (response.success && response.data?.user) {
          const userData = response.data.user;
          if (userData.personalInfo?.profilePicture) {
            setProfilePictureUrl(userData.personalInfo.profilePicture);
          } else {
            setProfilePictureUrl(null);
          }
          setViewedUserName({
            firstName: userData.personalInfo?.firstName || '',
            lastName: userData.personalInfo?.lastName || '',
          });
          setProfileData(mapUserToProfileData(userData));
        }
      } catch (err: any) {
        setError(err instanceof Error ? err.message : 'Failed to load profile');
        setProfilePictureUrl(null);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [userId, token]);

  const handleBackToDiscover = () => {
    try {
      navigation.goToDiscover();
    } catch {
      window.location.href = '/discover';
    }
  };

  return (
    <div className="min-h-screen bg-paper relative overflow-hidden">
      <BackgroundShapes />
      <div className="container mx-auto px-4 py-8 relative z-10">
        <div className="max-w-3xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-2xl font-bold text-coffee">View Profile</h1>
            <button
              onClick={handleBackToDiscover}
              className="bg-white border-2 border-coffee text-coffee rounded-xl px-4 py-2 font-bold shadow-sm hover:bg-cream transition-colors"
            >
              ← Back to Discover
            </button>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-100 border-2 border-red-400 rounded-lg">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <div className="bg-cream rounded-[32px] border-4 border-coffee shadow-[8px_8px_0px_#594A42] p-8 md:p-12 relative z-10 mb-8">
            <div className="flex flex-col items-center mb-8">
              <div className="mb-4">
                <Avatar
                  profilePictureUrl={profilePictureUrl}
                  firstName={viewedUserName?.firstName}
                  lastName={viewedUserName?.lastName}
                  userId={userId}
                  size="xlarge"
                  loading={loading}
                  containerClassName="border-4 border-white ring-2 ring-coffee shadow-md"
                />
              </div>
              <h2 className="text-4xl font-bold text-coffee mb-2">
                {viewedUserName ? `${viewedUserName.firstName} ${viewedUserName.lastName}` : '—'}
              </h2>
              {profileData?.occupation && (
                <p className="text-xl text-coffee/70 font-medium">
                  {occupations.find((o) => o.value === profileData.occupation)?.label || profileData.occupation}
                </p>
              )}
            </div>

            {loading ? (
              <div className="text-center py-8">
                <p className="text-coffee/70">Loading profile...</p>
              </div>
            ) : (
              <ProfileInfoSections profileData={profileData} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileViewPage;
